import { useState } from "react";
import { Check, Loader2, ThumbsDown, ThumbsUp, UserX } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, money } from "@/lib/format";

const STATUS = {
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  declined: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  withdrawn: "border-border bg-muted text-muted-foreground",
};

export const StatusChip = ({ status, testId }) => (
  <Chip testId={testId} className={STATUS[status] || STATUS.withdrawn}>{status}</Chip>
);

export const VoteBar = ({ progress, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <div className="flex h-2 overflow-hidden rounded-full bg-muted">
      <div className="bg-emerald-500 transition-all duration-500"
           style={{ width: `${progress.percent}%` }} />
      <div className="bg-red-500 transition-all duration-500"
           style={{ width: `${progress.decline_percent}%` }} />
    </div>
    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
        {progress.approvals} for
      </span>
      <span className="font-semibold text-red-600 dark:text-red-400">
        {progress.declines} against
      </span>
      <span>
        {progress.mode === "majority"
          ? `${progress.required} of ${progress.electorate} trustees needed`
          : `slab ${progress.slab} · ${progress.required} signature(s) needed`}
      </span>
    </div>
  </div>
);

export const ApprovalRow = ({ item, onDone, testId, children }) => {
  const { can } = useAuth();
  const [open, setOpen] = useState("");
  const [minute, setMinute] = useState("");
  const [busy, setBusy] = useState(false);
  const mayVote = can("approval", "vote") && item.you_can_vote;

  const vote = async (decision) => {
    setBusy(true);
    try {
      const res = await api.post(`/approvals/${item.request_ref}/vote`, { decision, minute });
      toast.success(res.data.status === "pending"
        ? `Your ${decision === "approve" ? "approval" : "decline"} is recorded — ${res.data.progress.approvals} for, ${res.data.progress.declines} against`
        : `Request ${res.data.status}`);
      setOpen("");
      setMinute("");
      await onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="panel space-y-3 p-4" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-muted-foreground">{item.request_ref}</span>
            <StatusChip status={item.status} testId={`status-${item.request_ref}`} />
            {item.anonymous && (
              <Chip className="border-border bg-muted text-muted-foreground">anonymous vote</Chip>
            )}
          </div>
          <div className="mt-1 truncate text-sm font-semibold text-foreground">{item.subject}</div>
          <div className="text-[11.5px] text-muted-foreground">
            {item.kind_label} · {money(item.amount)} · raised by {item.raised_by} · {dateTime(item.created_at)}
          </div>
          <p className="mt-1.5 max-w-2xl text-xs text-muted-foreground">{item.reason}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {item.you_recused && (
            <Chip className="border-border bg-muted text-muted-foreground">
              <UserX className="h-3 w-3" /> recused
            </Chip>
          )}
          {mayVote && (
            <>
              <Button size="sm" data-testid={`approve-${item.request_ref}`}
                      onClick={() => setOpen("approve")}
                      className="h-8 rounded-full bg-emerald-600 text-white hover:bg-emerald-600/90">
                <ThumbsUp className="mr-1 h-3.5 w-3.5" /> Approve
              </Button>
              <Button size="sm" variant="outline" data-testid={`decline-${item.request_ref}`}
                      onClick={() => setOpen("decline")}
                      className="h-8 rounded-full border-red-500/40 text-red-600 dark:text-red-400">
                <ThumbsDown className="mr-1 h-3.5 w-3.5" /> Decline
              </Button>
            </>
          )}
          {item.status === "pending" && can("approval", "vote") && !item.you_can_vote
            && !item.you_recused && (
            <Chip className="border-border bg-muted text-muted-foreground">
              <Check className="h-3 w-3" /> your decision is in
            </Chip>
          )}
        </div>
      </div>

      <VoteBar progress={item.progress} testId={`bar-${item.request_ref}`} />

      {item.progress.outstanding_roles.length > 0 && (
        <div className="text-[11px] text-muted-foreground">
          Still waiting on: {item.progress.outstanding_roles.join(", ").replace(/_/g, " ")}
        </div>
      )}

      {item.escalated && (
        <div className="text-[11px] text-amber-600 dark:text-amber-400" data-testid={`escalated-${item.request_ref}`}>
          {item.escalated}
        </div>
      )}

      {item.votes.length > 0 && (
        <div className="space-y-1.5 rounded-lg border border-border bg-muted/30 p-2.5">
          {item.votes.map((v, i) => (
            <div key={i} className="flex gap-2 text-[11.5px]" data-testid={`vote-${item.request_ref}-${i}`}>
              <span className={v.decision === "approve"
                ? "font-semibold text-emerald-600 dark:text-emerald-400"
                : "font-semibold text-red-600 dark:text-red-400"}>
                {v.decision === "approve" ? "For" : "Against"}
              </span>
              <span className="text-muted-foreground">
                {v.by ? `${v.by}${v.role_label ? ` (${v.role_label})` : ""}` : "identity withheld"}
                {v.mine ? " · you" : ""} · {dateTime(v.at)}
              </span>
              <span className="flex-1 truncate text-foreground/80">“{v.minute}”</span>
            </div>
          ))}
        </div>
      )}

      {children}

      <Dialog open={!!open} onOpenChange={(o) => setOpen(o ? open : "")}>        <DialogContent className="max-w-md border-border bg-card">
          <DialogHeader>
            <DialogTitle>{open === "approve" ? "Approve" : "Decline"} {item.request_ref}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {item.subject} · {money(item.amount)}. Your minute goes into the board record and the
            audit trail, even when voting is anonymous.
          </p>
          <Textarea data-testid="vote-minute-input" value={minute} rows={4}
                    onChange={(e) => setMinute(e.target.value)}
                    placeholder="Minute for the record (at least 10 characters)"
                    className="bg-muted/40 text-sm" />
          <Button data-testid="vote-submit-button" disabled={busy || minute.trim().length < 10}
                  onClick={() => vote(open)}
                  className={open === "approve"
                    ? "h-9 rounded-full bg-emerald-600 text-white hover:bg-emerald-600/90"
                    : "h-9 rounded-full bg-red-600 text-white hover:bg-red-600/90"}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Record my {open === "approve" ? "approval" : "decline"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const SlabTable = ({ slabs }) => (
  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="slab-table">
    {slabs.map((s) => (
      <div key={s.slab} className="rounded-xl border border-border bg-muted/30 p-3"
           data-testid={`slab-${s.slab}`}>
        <div className="kicker">Slab {s.slab}</div>
        <div className="mt-1 text-sm font-semibold text-foreground">
          {money(s.from, "INR", true)} – {s.to ? money(s.to, "INR", true) : "and above"}
        </div>
        <div className="mt-1 text-[11.5px] text-muted-foreground">{s.label}</div>
      </div>
    ))}
  </div>
);
